"use client";

import type { ButtonHTMLAttributes, ReactNode } from "react";

import { forwardRef } from "react";

import { buildTrackerProps, mergeClasses } from "@trex-2b/libs";

/** 对应 Figma：按钮形态（propValue1） */
export type ButtonVariant = "Fill" | "Border";

/** 对应 Figma：按钮状态（propValue） */
export type ButtonState = "Default" | "Hover" | "Disabled";

/** 对应 Figma：按钮尺寸（size） */
export type ButtonSize = "M" | "L" | "XL";

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  /** Figma: propValue1 */
  variant?: ButtonVariant;
  /** Figma: propValue（可用于在 Story/截图中强制呈现 Hover/Disabled） */
  state?: ButtonState;
  size?: ButtonSize;
  /** 左侧图标节点（可由调用方传默认图标） */
  leftIcon?: ReactNode;
  /** 右侧图标节点 */
  rightIcon?: ReactNode;
  /** 是否占满容器宽度 */
  fullWidth?: boolean;
};

const sizeLayout: Record<ButtonSize, string> = {
  // Figma M: h=32, px≈10.7, py≈5.3, gap≈5.3, radius≈26.7, font≈10.8, line≈20.7
  M: "h-8 gap-[5.333px] rounded-[26.667px] px-[10.667px] py-[5.333px] text-[10.757px] font-semibold leading-[20.707px] tracking-[-0.2017px]",
  // Figma L / XL: px=16, py=8, gap=8, radius=40, font=16, line=30.8
  L: "gap-2 rounded-[40px] px-4 py-2 text-base font-semibold leading-[30.8px] tracking-[-0.3px]",
  XL: "gap-2 rounded-[40px] px-4 py-2 text-base font-semibold leading-[30.8px] tracking-[-0.3px]",
};

const iconSlot: Record<ButtonSize, string> = {
  // Figma M icon size ≈ 16.136
  M: "inline-flex size-[16.136px] shrink-0 items-center justify-center [&_svg]:size-[16.136px]",
  L: "inline-flex size-6 shrink-0 items-center justify-center [&_svg]:size-6",
  XL: "inline-flex size-6 shrink-0 items-center justify-center [&_svg]:size-6",
};

function variantClasses(
  variant: ButtonVariant,
  state: ButtonState,
  size: ButtonSize,
): string {
  const isDisabled = state === "Disabled";
  const isHover = state === "Hover";

  if (variant === "Fill") {
    return mergeClasses(
      "text-white",
      isDisabled
        ? "bg-[#434343] text-[rgba(255,255,255,0.6)]"
        : isHover
          ? "bg-[#32302e]"
          : "bg-[#1c1c1c]",
      // default interactive behavior (when not forcing state)
      state === "Default" &&
        "hover:bg-[#32302e] disabled:bg-[#434343] disabled:text-[rgba(255,255,255,0.6)] disabled:hover:bg-[#434343]",
    );
  }

  // Border: 设计稿里 XL 的边框更“黑”（节点 26:965），其余用 rgba(28,25,22,0.4)
  const borderDefault =
    size === "XL"
      ? "border border-[#1c1c1c]"
      : "border border-[rgba(28,25,22,0.4)]";

  const baseBg = isDisabled || isHover ? "bg-[#e8e8e8]" : "bg-transparent";

  return mergeClasses(
    "text-[#1c1916]",
    borderDefault,
    baseBg,
    isDisabled && "text-[rgba(28,25,22,0.3)]",
    // default interactive behavior (when not forcing state)
    state === "Default" &&
      "hover:bg-[#e8e8e8] hover:border-[rgba(28,25,22,0.4)] disabled:bg-[#e8e8e8] disabled:border-[rgba(28,25,22,0.4)] disabled:text-[rgba(28,25,22,0.3)] disabled:hover:bg-[#e8e8e8] disabled:hover:border-[rgba(28,25,22,0.4)]",
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = "Border",
      state = "Default",
      size = "L",
      className,
      leftIcon,
      rightIcon,
      fullWidth,
      disabled,
      children,
      type = "button",
      ...rest
    },
    ref,
  ) {
    const computedDisabled = disabled || state === "Disabled";

    return (
      <button
        ref={ref}
        type={type}
        disabled={computedDisabled}
        className={mergeClasses(
          "inline-flex items-center justify-center transition-colors cursor-pointer",
          "[font-feature-settings:'ss01'_1,'cv01'_1,'cv11'_1]",
          sizeLayout[size],
          variantClasses(variant, state, size),
          fullWidth && "w-full",
          "disabled:cursor-not-allowed",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent",
          className,
        )}
        {...buildTrackerProps()}
        {...rest}
      >
        {leftIcon != null ? (
          <span className={iconSlot[size]}>{leftIcon}</span>
        ) : null}
        {children != null ? <span className="min-w-0">{children}</span> : null}
        {rightIcon != null ? (
          <span className={iconSlot[size]}>{rightIcon}</span>
        ) : null}
      </button>
    );
  },
);

Button.displayName = "Button";
